// Drift Auditor -- the top-of-results conversion diagnosis banner.

import React from 'react';
import { AlertTriangle, TrendingDown, Target, ShieldAlert } from 'lucide-react';

import type { ConversionDiagnosisBanner } from '../types';

const fmtMoney = (v: number): string =>
    `$${Math.round(v).toLocaleString('en-US')}`;

export const ConversionBanner: React.FC<{ banner: ConversionDiagnosisBanner }> = ({ banner }) => (
    <div className="relative overflow-hidden rounded-2xl border border-rose-200 bg-gradient-to-br from-rose-50 via-white to-amber-50 p-6 shadow-sm">
        <div className="flex items-start gap-4">
            <div className="shrink-0 mt-0.5 rounded-xl bg-rose-100 p-2.5 text-rose-600">
                <AlertTriangle className="h-6 w-6" aria-hidden="true" />
            </div>
            <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-extrabold text-slate-900 leading-snug">
                    {banner.headline}
                </h2>
                <p className="mt-1.5 text-sm text-slate-600">{banner.subheadline}</p>
            </div>
        </div>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-xl bg-white/80 border border-slate-100 p-4">
                <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-500">
                    <TrendingDown className="h-3.5 w-3.5" /> Wealth Lost to Drift
                </div>
                <p className="mt-1 text-2xl font-bold text-rose-600">
                    {fmtMoney(banner.lost_wealth_to_drift_dollars)}
                </p>
            </div>
            <div className="rounded-xl bg-white/80 border border-slate-100 p-4">
                <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-500">
                    <TrendingDown className="h-3.5 w-3.5" /> Variance Drain
                </div>
                <p className="mt-1 text-2xl font-bold text-amber-600">
                    {fmtMoney(banner.variance_drain_dollars)}
                </p>
            </div>
            <div className="rounded-xl bg-white/80 border border-slate-100 p-4">
                <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-500">
                    <ShieldAlert className="h-3.5 w-3.5" /> Excess Crash Risk
                </div>
                <p className="mt-1 text-2xl font-bold text-orange-600">
                    {fmtMoney(banner.excess_crash_exposure_dollars)}
                </p>
            </div>
            <div className="rounded-xl bg-white/80 border border-slate-100 p-4">
                <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-500">
                    <Target className="h-3.5 w-3.5" /> Biggest Bloat
                </div>
                <p className="mt-1 text-2xl font-bold text-slate-800">
                    {banner.max_single_asset_bloat_ticker}{' '}
                    <span className="text-base font-semibold text-slate-500">
                        {banner.max_single_asset_bloat_pct > 0 ? '+' : ''}
                        {banner.max_single_asset_bloat_pct.toFixed(1)}%
                    </span>
                </p>
            </div>
        </div>
    </div>
);
